import React, { useState, useEffect, useCallback } from 'react';
import { 
  Trash2, Shield, Users, Mail, Calendar, Check, X, Crown, Clock, Lock, Unlock, RefreshCw, 
  ShieldAlert, ShieldCheck, Search, Filter, ChevronDown, 
  Download, ChevronLeft, ChevronRight,
  UserCheck, UserX, Building, Zap
} from 'lucide-react';
import api from '../../lib/api';
import ConfirmModal from '../../components/ConfirmModal';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const ROLE_BADGES = {
  super_admin: { label: 'Super Admin', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', icon: <Crown size={12} /> },
  admin: { label: 'Admin', color: '#7c3aed', bg: 'rgba(124, 58, 237, 0.15)', icon: <ShieldAlert size={12} /> },
  user: { label: 'User', color: 'var(--text-secondary)', bg: 'var(--bg-tertiary)', icon: null },
};

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [changingRole, setChangingRole] = useState(null);
  const [stats, setStats] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  
  // Search & Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    plan: '',
    verified: '',
    onboarded: '',
    subscription_status: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const limit = 25;

  const isSuperAdmin = currentUser?.role === 'super_admin';

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, sort_by: sortBy, sort_order: sortOrder };
      if (searchTerm) params.search = searchTerm;
      if (filters.role) params.role = filters.role;
      if (filters.plan) params.plan = filters.plan;
      if (filters.verified) params.verified = filters.verified;
      if (filters.onboarded) params.onboarded = filters.onboarded;
      if (filters.subscription_status) params.subscription_status = filters.subscription_status;
      
      const res = await api.get('/api/auth/admin/users', { params });
      setUsers(res.data.users || []);
      setTotalUsers(res.data.total);
      setHasMore(res.data.has_more);
      setStats(res.data.stats);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [page, limit, sortBy, sortOrder, searchTerm, filters]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      loadUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filters]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ role: '', plan: '', verified: '', onboarded: '', subscription_status: '' });
    setPage(1);
  };

  const hasActiveFilters = searchTerm || filters.role || filters.plan || filters.verified || filters.onboarded || filters.subscription_status;

  const handleExport = async () => {
    try {
      const res = await api.get('/api/admin/users/export');
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ileadx_users.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Users exported');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Export failed');
    }
  };

  const handleToggleLock = (userId, currentLocked, userEmail) => {
    const action = currentLocked ? 'unlock' : 'lock';
    setConfirmDialog({
      title: `${action.toUpperCase()} Account`,
      message: `Are you sure you want to ${action} ${userEmail}?`,
      confirmLabel: action.toUpperCase(),
      confirmColor: currentLocked ? 'var(--success)' : 'var(--error)',
      onConfirm: async () => {
        try {
          await api.post(`/api/admin/users/${userId}/toggle-lock`);
          setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_locked: !currentLocked } : u));
          toast.success(`User ${action}ed`);
        } catch (err) {
          toast.error(err.response?.data?.detail || `Failed to ${action} user`);
        }
        setConfirmDialog(null);
      }
    });
  };

  const handleResetAccount = (userId, userEmail) => {
    setConfirmDialog({
      title: 'Reset Account',
      message: `Reset ${userEmail}? This wipes ALL their data and usage. This cannot be undone.`,
      confirmLabel: 'Reset Account',
      confirmColor: 'var(--error)',
      onConfirm: async () => {
        try {
          await api.post(`/api/admin/users/${userId}/reset-account`);
          setUsers(prev => prev.map(u => u.id === userId ? { 
            ...u, is_locked: 0, email_verified: 1, onboarding_complete: 1,
            service_name: null, company_name: null, last_active: null,
          } : u));
          toast.success('Account reset successfully');
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to reset account');
        }
        setConfirmDialog(null);
      }
    });
  };

  const handleDelete = (userId, userEmail, userRole) => {
    if (userRole === 'super_admin') {
      toast.error('Super admin cannot be deleted');
      return;
    }
    setConfirmDialog({
      title: 'Delete User',
      message: `Delete ${userEmail} and ALL their data? This is permanent and cannot be undone.`,
      confirmLabel: 'Delete Permanently',
      confirmColor: 'var(--error)',
      onConfirm: async () => {
        setDeleting(userId);
        try {
          await api.delete(`/api/admin/users/${userId}`);
          setUsers(prev => prev.filter(u => u.id !== userId));
          setTotalUsers(prev => prev - 1);
          toast.success(`Deleted ${userEmail}`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete user'); }
        finally { setDeleting(null); setConfirmDialog(null); }
      }
    });
  };

  const handleRoleChange = async (userId, newRole) => {
    setChangingRole(userId);
    try {
      await api.put('/api/admin/role', { user_id: userId, role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('Role updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update role'); }
    finally { setChangingRole(null); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-xl)' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Users</h2>
        <button onClick={handleExport} style={{
          padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-primary)',
          background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
          fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: 'var(--space-md)', marginBottom: 'var(--space-xl)',
        }}>
          <MiniStat icon={<Users size={18} />} label="Total Users" value={stats.total_users} color="var(--info)" />
          <MiniStat icon={<Crown size={18} />} label="Super Admins" value={stats.super_admins} color="#f59e0b" />
          <MiniStat icon={<ShieldAlert size={18} />} label="Admins" value={stats.admins} color="var(--accent-secondary)" />
          <MiniStat icon={<UserCheck size={18} />} label="Verified" value={stats.verified} color="var(--success)" />
          <MiniStat icon={<Zap size={18} />} label="Free" value={stats.free_plan} color="var(--text-secondary)" />
          <MiniStat icon={<Zap size={18} />} label="Growth" value={stats.growth_plan} color="var(--accent-primary)" />
          <MiniStat icon={<Zap size={18} />} label="Pro" value={stats.pro_plan} color="var(--accent-secondary)" />
          <MiniStat icon={<Calendar size={18} />} label="New Today" value={stats.new_today || 0} color="var(--success)" />
          <MiniStat icon={<Calendar size={18} />} label="New This Week" value={stats.new_this_week || 0} color="var(--accent-primary)" />
          <MiniStat icon={<Building size={18} />} label="Enterprise" value={stats.enterprise_plan} color="#f59e0b" />
        </div>
      )}

      {/* Search & Filter Bar */}
      <div style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-xl)', padding: 'var(--space-lg) var(--space-xl)',
        marginBottom: 'var(--space-xl)', display: 'flex', flexWrap: 'wrap',
        gap: 'var(--space-md)', alignItems: 'center',
      }}>
        <div style={{
          flex: '1 1 250px', display: 'flex', alignItems: 'center', gap: 10,
          background: 'var(--input-bg)', borderRadius: 'var(--radius-md)',
          padding: '8px 14px', border: '1px solid var(--border-primary)',
        }}>
          <Search size={16} color="var(--text-secondary)" />
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, company..."
            style={{ flex: 1, border: 'none', background: 'none', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}
          />
        </div>

        <button onClick={() => setShowFilters(!showFilters)} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
          borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)',
          background: showFilters ? 'var(--accent-glow)' : 'transparent',
          color: showFilters ? 'var(--accent-primary)' : 'var(--text-secondary)',
          cursor: 'pointer', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap',
        }}>
          <Filter size={14} /> Filters
          {hasActiveFilters && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-primary)' }} />}
        </button>

        {hasActiveFilters && (
          <button onClick={clearFilters} style={{
            padding: '8px 14px', borderRadius: 'var(--radius-md)',
            border: '1px solid var(--error)', background: 'transparent',
            color: 'var(--error)', cursor: 'pointer', fontSize: 12,
          }}>
            Clear
          </button>
        )}

        <span style={{ color: 'var(--text-tertiary)', fontSize: 12, marginLeft: 'auto' }}>
          {totalUsers} user{totalUsers !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 'var(--space-md)', marginBottom: 'var(--space-xl)',
          padding: 'var(--space-lg)', background: 'var(--bg-secondary)',
          border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)',
        }}>
          <div>
            <label style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 6, display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Role</label>
            <select value={filters.role} onChange={(e) => setFilters({...filters, role: e.target.value})}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}>
              <option value="">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
          </div>
          <div>
            <label style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 6, display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Plan</label>
            <select value={filters.plan} onChange={(e) => setFilters({...filters, plan: e.target.value})}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}>
              <option value="">All Plans</option>
              <option value="free">Free</option>
              <option value="growth">Growth</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div>
            <label style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 6, display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Email Verified</label>
            <select value={filters.verified} onChange={(e) => setFilters({...filters, verified: e.target.value})}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}>
              <option value="">All</option>
              <option value="yes">Verified</option>
              <option value="no">Not Verified</option>
            </select>
          </div>
          <div>
            <label style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 6, display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Onboarded</label>
            <select value={filters.onboarded} onChange={(e) => setFilters({...filters, onboarded: e.target.value})}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}>
              <option value="">All</option>
              <option value="yes">Onboarded</option>
              <option value="no">Not Onboarded</option>
            </select>
          </div>
          <div>
            <label style={{ color: 'var(--text-secondary)', fontSize: 11, marginBottom: 6, display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Subscription</label>
            <select value={filters.subscription_status} onChange={(e) => setFilters({...filters, subscription_status: e.target.value})}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="cancelled">Cancelled</option>
              <option value="past_due">Past Due</option>
            </select>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)', boxShadow: 'var(--shadow-sm)' }}>
        <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-primary)' }}>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => handleSort('full_name')}>User{getSortIcon('full_name')}</th>
              <th style={thStyle}>Service</th>
              <th style={thStyle}>Verified</th>
              <th style={thStyle}>Onboarded</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => handleSort('created_at')}>Joined{getSortIcon('created_at')}</th>
              <th style={thStyle}>Last Active</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => handleSort('role')}>Role{getSortIcon('role')}</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => handleSort('plan')}>Plan{getSortIcon('plan')}</th>
              {isSuperAdmin && <th style={thStyle}>Role</th>}
              <th style={thStyle}>Subscription</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40 }}><div className="loader"></div></td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>No users found{hasActiveFilters ? ' matching your filters' : ''}.</td></tr>
            ) : (
              users.map(user => {
                const roleBadge = ROLE_BADGES[user.role || 'user'];
                const canDelete = user.role !== 'super_admin' && !(user.role === 'admin' && !isSuperAdmin);
                const canChangeRole = isSuperAdmin && user.role !== 'super_admin';
                const planColors = { free: 'var(--text-secondary)', growth: 'var(--accent-primary)', pro: 'var(--accent-secondary)', enterprise: '#f59e0b' };

                return (
                  <tr key={user.id} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                    <td style={{ ...tdStyle, cursor: 'pointer' }} onClick={() => navigate(`/dashboard/admin/users/${user.id}`)}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {user.full_name || 'N/A'}
                        {currentUser?.id === user.id && <span style={{ fontSize: 10, background: 'var(--info-bg)', color: 'var(--info)', padding: '1px 6px', borderRadius: 8, marginLeft: 8 }}>You</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} /> {user.email}</div>
                      {user.company_name && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{user.company_name}</div>}
                    </td>
                    <td style={tdStyle}><span style={{ fontSize: 12, color: 'var(--success)' }}>{user.service_name || ', '}</span></td>
                    <td style={tdStyle}>{user.email_verified ? <Check size={14} color="var(--success)" /> : <X size={14} color="var(--error)" />}</td>
                    <td style={tdStyle}>{user.onboarding_complete ? <Check size={14} color="var(--success)" /> : <X size={14} color="var(--warning)" />}</td>
                    <td style={{ ...tdStyle, fontSize: 12, color: 'var(--text-secondary)' }}>{formatDate(user.created_at)}</td>
                    <td style={{ ...tdStyle, fontSize: 12, color: user.last_active ? 'var(--text-secondary)' : 'var(--text-tertiary)' }}>{user.last_active ? formatDate(user.last_active) : 'Never'}</td>
                    <td style={tdStyle}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: roleBadge.bg, color: roleBadge.color }}>
                        {roleBadge.icon}{roleBadge.label}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: `${planColors[user.plan || 'free']}15`, color: planColors[user.plan || 'free'], textTransform: 'capitalize' }}>
                        {user.plan || 'free'}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td style={tdStyle}>
                        {canChangeRole ? (
                          <select value={user.role || 'user'} onChange={(e) => handleRoleChange(user.id, e.target.value)} disabled={changingRole === user.id}
                            style={{ padding: '4px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}>
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                        ) : <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>, </span>}
                      </td>
                    )}
                    <td style={tdStyle}>
                      <span style={{ 
                        padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: (user.subscription_status || 'active') === 'active' ? 'var(--success-bg)' 
                                   : (user.subscription_status || 'active') === 'cancelled' ? 'var(--error-bg)' 
                                   : 'var(--warning-bg)',
                        color: (user.subscription_status || 'active') === 'active' ? 'var(--success)' 
                              : (user.subscription_status || 'active') === 'cancelled' ? 'var(--error)' 
                              : 'var(--warning)',
                        textTransform: 'capitalize'
                      }}>
                        {user.subscription_status || 'active'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button onClick={() => handleToggleLock(user.id, user.is_locked, user.email)}
                          disabled={user.role === 'super_admin' || (user.role === 'admin' && !isSuperAdmin)}
                          title={user.is_locked ? 'Unlock account' : 'Lock account'}
                          style={{
                            padding: '5px 8px', fontSize: 11, borderRadius: 'var(--radius-sm)',
                            border: user.is_locked ? '1px solid var(--error)' : '1px solid var(--border-primary)',
                            background: user.is_locked ? 'var(--error-bg)' : 'transparent',
                            color: user.is_locked ? 'var(--error)' : 'var(--text-tertiary)',
                            cursor: 'pointer', transition: 'all 0.2s',
                            opacity: (user.role === 'super_admin' || (user.role === 'admin' && !isSuperAdmin)) ? 0.3 : 1,
                          }}>
                          {user.is_locked ? <Lock size={12} /> : <Unlock size={12} />}
                        </button>
                        <button onClick={() => handleResetAccount(user.id, user.email)}
                          disabled={user.role === 'super_admin'}
                          title="Reset account (wipes data)"
                          style={{
                            padding: '5px 8px', fontSize: 11, borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border-primary)', background: 'transparent',
                            color: 'var(--warning)', cursor: user.role === 'super_admin' ? 'not-allowed' : 'pointer',
                            opacity: user.role === 'super_admin' ? 0.3 : 1,
                          }}>
                          <RefreshCw size={12} />
                        </button>
                        <button onClick={() => handleDelete(user.id, user.email, user.role)}
                          disabled={deleting === user.id || !canDelete}
                          title={user.role === 'super_admin' ? 'Super admin cannot be deleted' : 'Delete user'}
                          style={{
                            padding: '5px 8px', fontSize: 11, borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--error)', background: 'transparent',
                            color: canDelete ? 'var(--error)' : 'var(--text-tertiary)',
                            cursor: canDelete ? 'pointer' : 'not-allowed', opacity: canDelete ? 1 : 0.4,
                          }}>
                          {deleting === user.id ? '...' : <Trash2 size={12} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 'var(--space-xl)' }}>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
          style={{ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'transparent', color: page === 1 ? 'var(--text-tertiary)' : 'var(--text-secondary)', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}>
          <ChevronLeft size={16} /> Prev
        </button>
        <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Page {page} · {totalUsers} users</span>
        <button onClick={() => setPage(p => p + 1)} disabled={!hasMore}
          style={{ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'transparent', color: hasMore ? 'var(--text-secondary)' : 'var(--text-tertiary)', cursor: hasMore ? 'pointer' : 'not-allowed', opacity: hasMore ? 1 : 0.5 }}>
          Next <ChevronRight size={16} />
        </button>
      </div>

      {confirmDialog && (
        <ConfirmModal
          open={!!confirmDialog}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          confirmColor={confirmDialog.confirmColor}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}

function MiniStat({ icon, label, value, color }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-md)', padding: 'var(--space-md)', textAlign: 'center' }}>
      <div style={{ color, marginBottom: 4, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 10, marginTop: 2 }}>{label}</div>
    </div>
  );
}

const thStyle = { padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 14px', whiteSpace: 'nowrap' };